// Home: what needs you, what is running, and whether the host is healthy (design/isomorphic
// dashboard). Runs, environments and devices come from the existing endpoints; capacity, spend,
// broker calls and open questions from the dashboard endpoints, each widget on its own.
import { Play } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { devicesQuery, environmentsQuery, runsQuery, workflowsQuery } from "../api/queries";
import { meQuery } from "../auth/api";
import { brokerCallsQuery, usageQuery } from "../dashboard/api";
import { deniedCount, lastDay } from "../dashboard/broker";
import { BrokerCallsWidget } from "../dashboard/BrokerCallsWidget";
import { EnvironmentsWidget } from "../dashboard/EnvironmentsWidget";
import { greeting } from "../dashboard/format";
import { HostCapacityWidget } from "../dashboard/HostCapacityWidget";
import { Metrics } from "../dashboard/Metrics";
import { ModelSpendWidget } from "../dashboard/ModelSpendWidget";
import { CredentialsNote, PendingDeviceNotice, QuestionNotice } from "../dashboard/Notices";
import { countRuns, summary } from "../dashboard/runs";
import { RunsWidget } from "../dashboard/RunsWidget";
import { useAttention } from "../layout/attention";
import { useCurrentOrg } from "../layout/currentOrg";
import { PageHeader } from "../ui/PageHeader";
import { useT } from "../i18n";

const button = "inline-flex h-10 items-center gap-1.5 rounded-lg px-4 text-sm font-semibold transition-colors";

function name(email: string | undefined): string {
  const local = email?.split("@")[0]?.split(/[._+-]/)[0] ?? "";
  return local.charAt(0).toUpperCase() + local.slice(1);
}

export default function HomePage() {
  const t = useT().dashboard;
  const me = useQuery(meQuery());
  const { org, orgs } = useCurrentOrg();
  const orgId = org?.id;
  const runs = useQuery(runsQuery());
  const workflows = useQuery(workflowsQuery());
  const environments = useQuery(environmentsQuery());
  const devices = useQuery(devicesQuery());
  const usage = useQuery({ ...usageQuery(orgId ?? ""), enabled: orgId !== undefined });
  const calls = useQuery({ ...brokerCallsQuery(orgId ?? ""), enabled: orgId !== undefined });
  const { questions } = useAttention();
  const [now] = useState(Date.now);

  const orgRuns = runs.data && orgId ? runs.data.filter((r) => r.org_id === orgId) : runs.data;
  const counts = orgRuns ? countRuns(orgRuns) : undefined;
  const pendingDevice = devices.data?.find((d) => d.status === "pending");
  const recentCalls = calls.data ? lastDay(calls.data, now) : undefined;
  const who = name(me.data?.email);

  return (
    <>
      <PageHeader
        title={`${greeting(new Date().getHours())}${who ? `, ${who}` : ""}`}
        description={counts ? summary(counts) : t.checking}
      >
        <Link to="/devices" className={`${button} border border-gray-300 bg-gray-0 text-gray-900 hover:border-gray-1000 dark:bg-gray-50`}>
          {t.linkDevice}
        </Link>
        <Link to="/legacy-workflows" className={`${button} bg-primary text-primary-foreground hover:bg-primary-dark`}>
          <Play weight="fill" className="h-4 w-4" /> {t.startRun}
        </Link>
      </PageHeader>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <QuestionNotice questions={questions} className="xl:col-span-8" />
        <div className="flex flex-col gap-4 xl:col-span-4">
          {pendingDevice ? <PendingDeviceNotice device={pendingDevice} /> : null}
          <CredentialsNote calls={recentCalls?.length} denied={recentCalls ? deniedCount(recentCalls) : 0} />
        </div>

        <Metrics counts={counts} questions={questions} usage={usage.data} calls={calls.data} now={now} />

        <HostCapacityWidget className="xl:col-span-8" />
        <EnvironmentsWidget orgs={orgs} className="xl:col-span-4" />
        <RunsWidget runs={runs} orgId={orgId} workflows={workflows.data ?? []} environments={environments.data ?? []} className="xl:col-span-5" />
        <ModelSpendWidget orgId={orgId} className="xl:col-span-7" />
        <BrokerCallsWidget orgId={orgId} className="xl:col-span-12" />
      </div>
    </>
  );
}
